import { useEffect, useMemo, useState } from "react";
import { subscriptionService } from "../../services/subscriptionService";
import Icon from "../../components/Common/Icon";
import { useToast } from "../../context/ToastContext";

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" }) : "—";

/**
 * Admin — Paket Özellik Yönetimi.
 * Paket × özellik matrisi; admin checkbox'larla her paketin hangi özelliklere
 * sahip olduğunu dinamik olarak açıp kapatır ve kaydeder.
 */
export default function PackagesPage() {
  const { showToast } = useToast();
  const [packages, setPackages] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // packageId -> Set(featureKey)
  const [assigned, setAssigned] = useState({});

  // Editing state for package pricing/details modal
  const [editingPackage, setEditingPackage] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    monthlyPrice: "",
    maxStaff: "",
    maxServices: "",
    maxTasks: "",
    maxDocuments: "",
    description: ""
  });
  const [updating, setUpdating] = useState(false);
  // Düzenlenen paketin fiyat değişim geçmişi (en yeni önce).
  const [priceHistory, setPriceHistory] = useState([]);

  const startEdit = (pkg) => {
    setEditingPackage(pkg);
    setEditForm({
      name: pkg.name || "",
      monthlyPrice: pkg.monthlyPrice != null ? String(pkg.monthlyPrice) : "0",
      maxStaff: pkg.maxStaff != null ? String(pkg.maxStaff) : "10",
      maxServices: pkg.maxServices != null ? String(pkg.maxServices) : "10",
      maxTasks: pkg.maxTasks != null ? String(pkg.maxTasks) : "",
      maxDocuments: pkg.maxDocuments != null ? String(pkg.maxDocuments) : "",
      description: pkg.description || "",
      active: pkg.active ?? true
    });
    setPriceHistory([]);
    subscriptionService
      .priceHistory(pkg.id)
      .then((res) => setPriceHistory(res.data.data ?? []))
      .catch(() => setPriceHistory([]));
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    setUpdating(true);
    try {
      const res = await subscriptionService.update(editingPackage.id, {
        name: editForm.name,
        level: editingPackage.level,
        description: editForm.description,
        maxStaff: parseInt(editForm.maxStaff, 10),
        maxServices: parseInt(editForm.maxServices, 10),
        maxTasks: editForm.maxTasks ? parseInt(editForm.maxTasks, 10) : null,
        maxDocuments: editForm.maxDocuments ? parseInt(editForm.maxDocuments, 10) : null,
        monthlyPrice: parseFloat(editForm.monthlyPrice),
        features: Array.from(assigned[editingPackage.id] ?? []),
        active: editForm.active
      });

      // Update package in state
      setPackages((prev) =>
        prev.map((p) => (p.id === editingPackage.id ? res.data.data : p))
      );
      showToast("Paket başarıyla güncellendi.", "success");
      setEditingPackage(null);
    } catch (err) {
      showToast(err.response?.data?.error ?? "Güncellenemedi.", "error");
    } finally {
      setUpdating(false);
    }
  };

  useEffect(() => {
    Promise.all([subscriptionService.list(), subscriptionService.features()])
      .then(([pkgRes, featRes]) => {
        const pkgs = pkgRes.data.data ?? [];
        setPackages(pkgs);
        setCatalog(featRes.data.data ?? []);
        const map = {};
        pkgs.forEach((p) => {
          map[p.id] = new Set(p.features ?? []);
        });
        setAssigned(map);
      })
      .catch(() => showToast("Paketler yüklenemedi.", "error"))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Özellikleri kategoriye göre grupla (Rapor / Modül …)
  const grouped = useMemo(() => {
    const g = {};
    catalog.forEach((f) => {
      (g[f.category] ??= []).push(f);
    });
    return g;
  }, [catalog]);

  const toggle = (pkgId, key) => {
    setAssigned((prev) => {
      const next = new Set(prev[pkgId]);
      next.has(key) ? next.delete(key) : next.add(key);
      return { ...prev, [pkgId]: next };
    });
  };

  // Değişen paketleri bul (kaydet butonu için)
  const dirty = useMemo(() => {
    return packages.some((p) => {
      const orig = new Set(p.features ?? []);
      const cur = assigned[p.id] ?? new Set();
      if (orig.size !== cur.size) return true;
      for (const k of cur) if (!orig.has(k)) return true;
      return false;
    });
  }, [packages, assigned]);

  const save = async () => {
    setSaving(true);
    try {
      const changed = packages.filter((p) => {
        const orig = new Set(p.features ?? []);
        const cur = assigned[p.id] ?? new Set();
        if (orig.size !== cur.size) return true;
        for (const k of cur) if (!orig.has(k)) return true;
        return false;
      });
      await Promise.all(
        changed.map((p) =>
          subscriptionService.update(p.id, {
            name: p.name,
            level: p.level,
            description: p.description,
            maxStaff: p.maxStaff,
            maxServices: p.maxServices,
            maxTasks: p.maxTasks,
            maxDocuments: p.maxDocuments,
            monthlyPrice: p.monthlyPrice,
            features: Array.from(assigned[p.id] ?? []),
            active: p.active ?? true,
          })
        )
      );
      // Yerel "orijinal" durumu güncelle
      setPackages((prev) =>
        prev.map((p) => ({ ...p, features: Array.from(assigned[p.id] ?? []) }))
      );
      showToast("Paket özellikleri güncellendi.", "success");
    } catch (err) {
      showToast(err.response?.data?.error ?? "Kaydedilemedi.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
            <Icon name="workspace_premium" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-on-surface">Paket Yönetimi</h1>
            <p className="text-on-surface-variant text-sm">
              Her paketin sahip olduğu özellikleri checkbox'larla dinamik yönetin.
            </p>
          </div>
        </div>
        <button
          onClick={save}
          disabled={!dirty || saving}
          className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold bg-primary text-white hover:opacity-90 transition disabled:opacity-40"
        >
          <Icon name="save" className="!text-[18px]" />
          {saving ? "Kaydediliyor…" : "Kaydet"}
        </button>
      </div>

      {loading ? (
        <div className="bg-surface-card rounded-2xl p-10 border border-outline-variant text-center text-on-surface-variant">
          Yükleniyor…
        </div>
      ) : (
        <div className="bg-surface-card rounded-2xl border border-outline-variant overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead>
              <tr className="border-b border-outline-variant">
                <th className="text-left font-semibold text-on-surface-variant px-4 py-3 w-[45%]">
                  Özellik
                </th>
                {packages.map((p) => (
                  <th key={p.id} className="text-center font-bold text-on-surface px-4 py-3 relative">
                    <div className="flex items-center justify-center gap-1.5">
                      <span>{p.name}</span>
                      <button
                        onClick={() => startEdit(p)}
                        className="p-1 rounded-lg hover:bg-primary/10 text-primary transition flex items-center justify-center"
                        title="Paketi Düzenle"
                      >
                        <Icon name="edit" className="!text-[14px]" />
                      </button>
                    </div>
                    <span className="block text-[11px] font-normal text-on-surface-variant mt-0.5">
                      {Number(p.monthlyPrice).toLocaleString("tr-TR")}₺/ay
                    </span>
                    {p.active === false && (
                      <span className="inline-block text-[10px] font-bold text-rose-600 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5 mt-1">
                        Pasif (Kapalı)
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(grouped).map(([category, feats]) => (
                <FeatureGroup
                  key={category}
                  category={category}
                  feats={feats}
                  packages={packages}
                  assigned={assigned}
                  onToggle={toggle}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Package Modal */}
      {editingPackage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-surface-card rounded-3xl shadow-modal border border-outline-variant max-w-md w-full overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-outline-variant flex items-center justify-between bg-surface-container-low">
              <div className="flex items-center gap-2">
                <Icon name="edit" className="text-primary !text-[22px]" />
                <h3 className="font-bold text-base text-on-surface">Paketi Düzenle: {editingPackage.name}</h3>
              </div>
              <button
                type="button"
                onClick={() => setEditingPackage(null)}
                className="p-1 rounded-full text-on-surface-variant hover:text-primary transition"
              >
                <Icon name="close" className="!text-[22px]" />
              </button>
            </div>

            <form onSubmit={handleUpdateSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Paket Adı</label>
                <input
                  type="text"
                  required
                  value={editForm.name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full border border-outline-variant rounded-xl px-3 py-2 text-xs bg-bg-subtle/50 focus:ring-4 focus:ring-primary/15 focus:border-primary outline-none transition"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Aylık Ücret (₺)</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  min="0"
                  value={editForm.monthlyPrice}
                  onChange={(e) => setEditForm(prev => ({ ...prev, monthlyPrice: e.target.value }))}
                  className="w-full border border-outline-variant rounded-xl px-3 py-2 text-xs bg-bg-subtle/50 focus:ring-4 focus:ring-primary/15 focus:border-primary outline-none transition"
                />
                <p className="text-[10px] text-on-surface-variant mt-1">
                  Fiyat değişikliği yalnızca bugünden sonrası için geçerlidir; mevcut salonlar abone
                  oldukları fiyattan devam eder.
                </p>
              </div>

              {priceHistory.length > 0 && (
                <div className="rounded-xl border border-outline-variant bg-bg-subtle/40 p-3">
                  <p className="text-[11px] font-semibold text-on-surface-variant mb-2 flex items-center gap-1">
                    <Icon name="history" className="!text-[14px]" /> Fiyat Geçmişi
                  </p>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {priceHistory.map((h, i) => (
                      <div key={i} className="flex items-center justify-between text-[11px]">
                        <span className="text-on-surface-variant">
                          {fmtDate(h.effectiveFrom)} – {h.effectiveTo ? fmtDate(h.effectiveTo) : "güncel"}
                        </span>
                        <span className={`font-semibold ${h.effectiveTo ? "text-on-surface-variant" : "text-primary"}`}>
                          {Number(h.price).toLocaleString("tr-TR")}₺
                          {!h.effectiveTo && <span className="ml-1 text-[9px] uppercase tracking-wide">güncel</span>}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Maksimum Personel</label>
                  <input
                    type="number"
                    required
                    min="1"
                    value={editForm.maxStaff}
                    onChange={(e) => setEditForm(prev => ({ ...prev, maxStaff: e.target.value }))}
                    className="w-full border border-outline-variant rounded-xl px-3 py-2 text-xs bg-bg-subtle/50 focus:ring-4 focus:ring-primary/15 focus:border-primary outline-none transition"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Maksimum Hizmet</label>
                  <input
                    type="number"
                    required
                    min="1"
                    value={editForm.maxServices}
                    onChange={(e) => setEditForm(prev => ({ ...prev, maxServices: e.target.value }))}
                    className="w-full border border-outline-variant rounded-xl px-3 py-2 text-xs bg-bg-subtle/50 focus:ring-4 focus:ring-primary/15 focus:border-primary outline-none transition"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Maksimum Görev (To-Do) (Boş = Sınırsız)</label>
                  <input
                    type="number"
                    min="1"
                    placeholder="Sınırsız"
                    value={editForm.maxTasks}
                    onChange={(e) => setEditForm(prev => ({ ...prev, maxTasks: e.target.value }))}
                    className="w-full border border-outline-variant rounded-xl px-3 py-2 text-xs bg-bg-subtle/50 focus:ring-4 focus:ring-primary/15 focus:border-primary outline-none transition"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Maksimum Döküman (Boş = Sınırsız)</label>
                  <input
                    type="number"
                    min="1"
                    placeholder="Sınırsız"
                    value={editForm.maxDocuments}
                    onChange={(e) => setEditForm(prev => ({ ...prev, maxDocuments: e.target.value }))}
                    className="w-full border border-outline-variant rounded-xl px-3 py-2 text-xs bg-bg-subtle/50 focus:ring-4 focus:ring-primary/15 focus:border-primary outline-none transition"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Açıklama</label>
                <textarea
                  rows="2"
                  value={editForm.description}
                  onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full border border-outline-variant rounded-xl px-3 py-2 text-xs bg-bg-subtle/50 focus:ring-4 focus:ring-primary/15 focus:border-primary outline-none transition resize-none"
                />
              </div>

              <div className="flex items-center justify-between p-3.5 rounded-xl border border-outline-variant bg-surface-container-low">
                <div>
                  <span className="block text-xs font-bold text-on-surface">Paket Yayın Durumu</span>
                  <span className="block text-[11px] text-on-surface-variant">
                    Pasife alındığında yeni salonlar bu paketi seçemez/yükseltemez. Mevcut aboneler etkilenmez.
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setEditForm(prev => ({ ...prev, active: !prev.active }))}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer ${
                    editForm.active
                      ? "bg-emerald-500/15 text-emerald-700 border border-emerald-500/30"
                      : "bg-rose-500/15 text-rose-700 border border-rose-500/30"
                  }`}
                >
                  <Icon name={editForm.active ? "check_circle" : "cancel"} className="!text-[16px]" />
                  {editForm.active ? "Aktif" : "Pasif"}
                </button>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setEditingPackage(null)}
                  className="px-4 py-2 border border-outline-variant hover:bg-bg-subtle rounded-xl text-xs font-semibold transition"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={updating}
                  className="px-4 py-2 bg-primary text-white hover:opacity-95 disabled:opacity-40 rounded-xl text-xs font-semibold transition flex items-center gap-1.5"
                >
                  <Icon name="check" className="!text-[16px]" />
                  {updating ? "Güncelleniyor…" : "Kaydet"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function FeatureGroup({ category, feats, packages, assigned, onToggle }) {
  return (
    <>
      <tr className="bg-surface-container-low">
        <td
          colSpan={packages.length + 1}
          className="px-4 py-2 text-xs font-bold uppercase tracking-wide text-on-surface-variant"
        >
          {category}
        </td>
      </tr>
      {feats.map((f) => (
        <tr key={f.key} className="border-b border-outline-variant/50 last:border-0 hover:bg-surface-container-high/40">
          <td className="px-4 py-3">
            <p className="font-medium text-on-surface flex items-center gap-2">
              {f.label}
              {f.implemented === false && (
                <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-primary/10 text-primary">
                  Yakında
                </span>
              )}
            </p>
            {f.description && (
              <p className="text-xs text-on-surface-variant mt-0.5">{f.description}</p>
            )}
          </td>
          {packages.map((p) => {
            const checked = assigned[p.id]?.has(f.key) ?? false;
            return (
              <td key={p.id} className="text-center px-4 py-3">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggle(p.id, f.key)}
                  className="w-5 h-5 accent-primary cursor-pointer align-middle"
                  aria-label={`${p.name} — ${f.label}`}
                />
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}
